/**
 * crewCertCheck.js
 *
 * Pre-departure crew certification gap checker for FT-07.
 * Checks if selected crew collectively hold AT_LEAST_1 certificates
 * required by WBC3 for vessel-level requirements.
 *
 * FT-158: Added minimum manning, position-specific radar, and category-based first aid
 * FT-201: Master qualification now certificate-based (not position-based) via isMasterQualified()
 *
 * FleetSkipper - Built for Mariners.
 */

import { getQualifiedMasters, isMasterQualified } from './masterQualificationCheck';

// Certificate hierarchy - higher certificates that cover lower ones
// Duplicated from certificateHelpers.js for performance (avoid circular imports)
const CERTIFICATE_HIERARCHY = {
  // Radio certificates - GMDSS GOC > ROC > VHF DSC
  'VHF DSC Radio Operator': ['GMDSS GOC', 'GMDSS ROC'],
  'GMDSS ROC': ['GMDSS GOC'],

  // Engineering certificates hierarchy
  'Marine Engine Operator License': ['Senior Marine Engine Operator License', 'Small Vessel Second Engineer', 'STCW III/2 Small Vessel Chief Engineer'],
  'Senior Marine Engine Operator License': ['Small Vessel Second Engineer', 'STCW III/2 Small Vessel Chief Engineer'],
  'Small Vessel Second Engineer': ['STCW III/2 Small Vessel Chief Engineer'],
  'AEC Part 1': ['AEC Part 2', 'Small Vessel Second Engineer', 'STCW III/2 Small Vessel Chief Engineer']
};

/**
 * Parse category string to number (e.g., 'cat2' -> 2, 'Category 3' -> 3)
 */
const parseCategory = (category) => {
  if (typeof category === 'number') return category;
  if (!category) return 6; // Default to Cat 6 (most restricted)
  const match = String(category).match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 6;
};

/**
 * Check if any crew member holds a certificate (or a superior certificate)
 */
const anyCrewHasCert = (crewMembers, certNames) => {
  if (!Array.isArray(crewMembers) || crewMembers.length === 0) return false;

  // Build set of all acceptable certs (including superiors)
  const acceptableCerts = new Set();
  certNames.forEach(certName => {
    acceptableCerts.add(certName);
    // Add superior certificates that also satisfy this requirement
    const superiors = CERTIFICATE_HIERARCHY[certName] || [];
    superiors.forEach(sup => acceptableCerts.add(sup));
  });

  // Check if any crew member has any of the acceptable certs
  return crewMembers.some(crew => {
    const certs = crew.certificates || [];
    return certs.some(cert => {
      const certType = cert?.type || cert?.title || '';
      return acceptableCerts.has(certType);
    });
  });
};

/**
 * FT-158: Minimum Manning per WBC3 Table A5.1
 * Checks Master assignment and crew count requirements by category
 */
function checkMinimumManning(selectedCrewMembers, vesselSettings, masters) {
  const gaps = [];
  const areaCategory = parseCategory(vesselSettings.category);
  const singleHandedAllowed = vesselSettings.singleHandedEndorsed && areaCategory >= 3;

  // Check 1: Minimum crew count
  const minCrewRequired = singleHandedAllowed ? 1 : 2;
  if (selectedCrewMembers.length < minCrewRequired) {
    gaps.push({
      cert: 'Minimum Manning',
      message: singleHandedAllowed
        ? 'At least 1 crew member (Master) required.'
        : 'Minimum 2 crew required (Master + second person).',
      wbc3Ref: 'WBC3 Table A5.1'
    });
  }

  // Check 2: Second person qualifications (only if 2+ crew AND category requires it)
  if (selectedCrewMembers.length >= 2 && masters.length > 0) {
    const secondPersons = selectedCrewMembers.filter(c => !masters.includes(c));

    if (areaCategory === 0) {
      // Cat 0: Second person MUST hold one of these CoCs
      // FT-158 CORRECTION #1: Expanded list per regs agent
      const secondCoCs = [
        'Yachtmaster Offshore CoC', 'Yachtmaster Offshore',
        'Yachtmaster Ocean CoC', 'Yachtmaster Ocean',
        'STCW Master <200GT 150nm', 'Master <200GT 150nm',
        'STCW Master <200GT unlimited', 'Master <200GT unlimited',
        'STCW II/3 OOW', 'OOW'
      ];

      const qualifiedSecond = secondPersons.filter(p =>
        anyCrewHasCert([p], secondCoCs)
      );

      if (qualifiedSecond.length === 0) {
        gaps.push({
          cert: 'Second Person Qualification',
          message: 'Cat 0 requires second person to hold Yachtmaster Offshore/Ocean CoC or STCW Master <200GT.',
          wbc3Ref: 'WBC3 Table A5.1'
        });
      }
    } else if (areaCategory === 1) {
      // Cat 1: Second person needs Yachtmaster Coastal or higher
      const secondCoCs = [
        'Yachtmaster Coastal CoC', 'Yachtmaster Coastal',
        'Yachtmaster Offshore CoC', 'Yachtmaster Offshore',
        'Yachtmaster Ocean CoC', 'Yachtmaster Ocean'
      ];

      const qualifiedSecond = secondPersons.filter(p =>
        anyCrewHasCert([p], secondCoCs)
      );

      if (qualifiedSecond.length === 0) {
        gaps.push({
          cert: 'Second Person Qualification',
          message: 'Cat 1 requires second person to hold Yachtmaster Coastal CoC or equivalent.',
          wbc3Ref: 'WBC3 Table A5.1'
        });
      }
    } else if (areaCategory === 2) {
      // Cat 2: Second person "deemed competent by master" - confirmation prompt
      gaps.push({
        cert: 'Second Person Competence',
        message: 'Cat 2: Master must confirm second person is deemed competent.',
        wbc3Ref: 'WBC3 Table A5.1 - "deemed competent by the master"',
        blocking: false,  // Soft warning, need Master confirmation
        requiresConfirmation: true
      });
    }
    // Cat 3-6: No specific qualification for second person - just need 2 people
  }

  return gaps;
}

/**
 * Check for certification gaps in selected crew for pre-departure
 *
 * @param {Array} selectedCrewMembers - Full crew objects (with certificates array)
 * @param {Object} vesselSettings - Vessel settings { lengthOverall, category, enginePowerKW, ... }
 * @returns {Array} Array of gap objects: { cert, message, wbc3Ref }
 */
export function checkCrewCertGaps(selectedCrewMembers, vesselSettings) {
  const gaps = [];

  if (!vesselSettings) return gaps;

  const vesselLength = vesselSettings.lengthOverall || vesselSettings.length || 0;
  const areaCategory = parseCategory(vesselSettings.category);

  // FT-201: Master qualification check - CERTIFICATE-BASED (not position-based)
  // Uses isMasterQualified() to check CoC + Medical + Survival certificates
  // Position field is operational label only - certificates are source of truth
  const qualifiedMasterResults = getQualifiedMasters(selectedCrewMembers, vesselSettings);
  const masters = qualifiedMasterResults.map(r => r.crew);

  if (masters.length === 0) {
    // Provide detailed feedback on why no one qualifies
    const reasons = [];
    selectedCrewMembers.forEach(crew => {
      const result = isMasterQualified(crew, vesselSettings);
      if (!result.qualified && result.reasons.length > 0) {
        reasons.push(`${crew.name || crew.firstName || 'Unnamed'}: ${result.reasons[0]}`);
      }
    });

    gaps.push({
      cert: 'Qualified Master',
      message: 'No crew member holds valid Master certificates for this vessel category.',
      wbc3Ref: 'WBC3 Table A5.1 - Master must hold CoC valid for vessel category + medical + sea survival.',
      details: reasons.length > 0 ? reasons.slice(0, 3) : undefined  // Show first 3 reasons
    });
  } else {
    // Check for near-expiry warnings on qualified masters
    qualifiedMasterResults.forEach(({ crew, qualificationResult }) => {
      if (qualificationResult.warnings && qualificationResult.warnings.length > 0) {
        qualificationResult.warnings.forEach(warning => {
          gaps.push({
            cert: 'Master Certificate Warning',
            message: `${crew.name || crew.firstName}: ${warning}`,
            wbc3Ref: 'Certificate expiry reminder',
            blocking: false  // Warning only, doesn't prevent departure
          });
        });
      }
    });
  }

  // FT-158: Minimum Manning checks
  const manningGaps = checkMinimumManning(selectedCrewMembers, vesselSettings, masters);
  gaps.push(...manningGaps);

  // Fire Fighting (<15m only - larger vessels require ALL crew to hold it)
  if (vesselLength > 0 && vesselLength < 15) {
    if (!anyCrewHasCert(selectedCrewMembers, ['Fire Prevention & Fire Fighting'])) {
      gaps.push({
        cert: 'Fire Fighting',
        message: 'No assigned crew holds a Fire Fighting certificate.',
        wbc3Ref: 'WBC3 Table A5.3 requires at least one on vessels under 15m.'
      });
    }
  }

  // FT-158: First Aid / Medical Care per category
  if (areaCategory <= 1) {
    // Cat 0-1: At least one person must hold Proficiency in Medical Care
    const medicalCareCerts = [
      'Proficiency in Medical Care',
      'Medical Care Certificate',
      'STCW Medical Care'
    ];

    if (!anyCrewHasCert(selectedCrewMembers, medicalCareCerts)) {
      gaps.push({
        cert: 'Medical Care',
        message: 'Cat 0-1 requires at least one person with Proficiency in Medical Care certificate.',
        wbc3Ref: 'WBC3 Table A5.3 - Medical Care for unlimited area'
      });
    }
  } else if (areaCategory >= 2) {
    // Cat 2-6: At least one person holds Elementary First Aid
    if (!anyCrewHasCert(selectedCrewMembers, ['Elementary First Aid'])) {
      gaps.push({
        cert: 'First Aid',
        message: 'No assigned crew holds an Elementary First Aid certificate.',
        wbc3Ref: 'WBC3 Table A5.3 requires at least one onboard for Cat 2-6 vessels.'
      });
    }
  }

  // Engineering (Cat 0-2 only - Cat 3-6 has no formal cert requirement per Table A5.2)
  if (areaCategory <= 2) {
    const engineCerts = [
      'Marine Engine Operator License',
      'Marine Engine Operator License (MEOL)',
      'MEOL',
      'Senior Marine Engine Operator License',
      'Senior Marine Engine Operator License (SMEOL)',
      'SMEOL',
      'AEC Part 1',
      'AEC Part 2',
      'Small Vessel Second Engineer',
      'Small Vessel Second Engineer CoC',
      'STCW III/2 Small Vessel Chief Engineer',
      'Engineering Officer <9000kW',
      'Engineering Officer <3000kW'
    ];
    if (!anyCrewHasCert(selectedCrewMembers, engineCerts)) {
      gaps.push({
        cert: 'Engineering Qualification',
        message: 'No assigned crew holds an engineering qualification.',
        wbc3Ref: 'WBC3 Table A5.2 requires at least one for Cat 0-2 vessels.'
      });
    }
  }

  // Radio Operator (all vessels)
  if (!anyCrewHasCert(selectedCrewMembers, ['GMDSS GOC', 'GMDSS ROC', 'VHF DSC Radio Operator'])) {
    gaps.push({
      cert: 'Radio Operator',
      message: 'No assigned crew holds a radio operator certificate.',
      wbc3Ref: 'WBC3 Section 28.1.8 requires at least one qualified person.'
    });
  }

  // ========================================================================
  // FT-30: Equipment-Linked Conditional Cert Gaps
  // These only apply when specific equipment is fitted to the vessel
  // ========================================================================

  // Extract equipment config from vessel settings
  // FT-30 Fix: Also check user-selected equipment arrays from Step 6 wizard
  const navEquipmentArray = Array.isArray(vesselSettings.navigationEquipment) ? vesselSettings.navigationEquipment : [];

  // Combined equipment detection: check explicit flags OR equipment arrays
  const hasRadarFitted = vesselSettings.hasRadar || vesselSettings.optionalEquipment?.radar || navEquipmentArray.includes('radar');
  // FT-30 Part 2: Split ECS vs ECDIS — different qualifications per WBC3 Table A5.3
  // Chart plotter → ECS Training (MCA Small Ships Navigation: ECS and Bridge Watchkeeping)
  // Full ECDIS → ECDIS Training (Appropriate ECDIS Training per MIN 698)
  const hasChartPlotterFitted = vesselSettings.hasECDIS || vesselSettings.navcomms?.hasECDIS || navEquipmentArray.includes('chart_plotter');
  const hasECDISFitted = navEquipmentArray.includes('ecdis');  // Only explicit ECDIS, not chart plotter
  const hasCooking = vesselSettings.hasCooking || vesselSettings.accommodation?.hasCooking || vesselSettings.hasGalley;
  const operations = vesselSettings.operations?.operations || vesselSettings.operations || [];
  const operationsList = Array.isArray(operations) ? operations : [];

  // FT-158 CORRECTION #2: Radar/ARPA Training - Master + crew using radar
  // Master MUST hold it; label clarified per regs agent
  if (hasRadarFitted || areaCategory <= 2) {
    if (masters.length > 0) {
      const mastersWithoutRadar = masters.filter(master =>
        !anyCrewHasCert([master], ['Radar/ARPA Training', 'Radar Training', 'ARPA Training'])
      );

      if (mastersWithoutRadar.length > 0) {
        const names = mastersWithoutRadar.map(c => c.full_name || c.name).join(', ');
        gaps.push({
          cert: 'Radar/ARPA Training',
          message: `Master lacks Radar/ARPA training (required for radar use): ${names}`,
          wbc3Ref: 'WBC3 Table A5.3: Master + crew using radar must hold Radar/ARPA training.'
        });
      }
    }
  }

  // FT-30 Part 2: ECS vs ECDIS — Different qualifications per WBC3 Table A5.3
  // Chart plotter → ECS Training (MCA Small Ships Navigation: ECS and Bridge Watchkeeping)
  if (hasChartPlotterFitted) {
    if (!anyCrewHasCert(selectedCrewMembers, ['ECS Training', 'Electronic Chart System Training'])) {
      gaps.push({
        cert: 'ECS Training',
        message: 'No assigned crew holds ECS training.',
        wbc3Ref: 'WBC3 Table A5.3 requires at least one when electronic chart plotter fitted.'
      });
    }
  }

  // Full ECDIS → ECDIS Training (Appropriate ECDIS Training per MIN 698)
  // ECDIS is treated as equipment in excess of Code requirements (WBC3 19.3.4)
  if (hasECDISFitted) {
    if (!anyCrewHasCert(selectedCrewMembers, ['ECDIS Training'])) {
      gaps.push({
        cert: 'ECDIS Training',
        message: 'No assigned crew holds ECDIS training.',
        wbc3Ref: 'WBC3 Table A5.3 Note B: ECDIS requires appropriate training per MIN 698.'
      });
    }
  }

  // Catering Training: When vessel has galley/cooking
  if (hasCooking) {
    if (!anyCrewHasCert(selectedCrewMembers, ['Ship\'s Cook Certificate / Catering Training', 'Ship\'s Cook Certificate', 'Catering Training', 'Food Safety'])) {
      gaps.push({
        cert: 'Catering Training',
        message: 'No assigned crew holds catering/food safety certification.',
        wbc3Ref: 'WBC3 Table A5.3 requires at least one when cooking facilities fitted.'
      });
    }
  }

  // MGO Transfer Training: ALL crew must have it when vessel does fuel transfer
  // This is ALL_CREW scope, so check EVERY selected crew member
  if (operationsList.includes('fuel_transfer') || operationsList.includes('bunkering')) {
    const crewWithoutMGO = selectedCrewMembers.filter(crew => {
      return !anyCrewHasCert([crew], ['MGO Transfer Training', 'Fuel Transfer Training', 'Bunkering Training']);
    });
    if (crewWithoutMGO.length > 0) {
      const names = crewWithoutMGO.map(c => c.full_name || c.name).join(', ');
      gaps.push({
        cert: 'MGO Transfer Training',
        message: `${crewWithoutMGO.length} crew member(s) lack MGO Transfer training: ${names}`,
        wbc3Ref: 'WBC3 Section 29.10.2.5 requires ALL crew on vessels conducting fuel transfer.'
      });
    }
  }

  // FT-30 Part 3: Stability Training - Required for Master when vessel has SIB
  // This is position-specific: only check if Master IS selected (uses masters filtered above)
  // If no Master at all, the "No Master/Skipper" warning covers it
  const hasStabilityBooklet = vesselSettings.hasStabilityBooklet;
  if (hasStabilityBooklet && masters.length > 0) {
    const mastersWithoutStability = masters.filter(master => {
      return !anyCrewHasCert([master], ['Stability Training']);
    });
    if (mastersWithoutStability.length > 0) {
      const names = mastersWithoutStability.map(c => c.full_name || c.name).join(', ');
      gaps.push({
        cert: 'Stability Training',
        message: `Master/Skipper lacks Stability Training: ${names}`,
        wbc3Ref: 'WBC3 Table A5.3: Master must hold MCA-approved 1-day stability course when vessel requires SIB.'
      });
    }
  }

  return gaps;
}

export default checkCrewCertGaps;
